/**
 * Курс дня на экране «Интеграции» (docs/08-screens.md): последний снимок
 * P2P Bybit, курс ЦБ и откуда курс берётся прямо сейчас. Если P2P падает,
 * рублёвые суммы считаются по ЦБ — это должно быть видно здесь, а не только
 * в логах сервера (docs/04-finance.md).
 */
import { Badge, Card } from '@/ds';
import { Provenance } from '@/components/money';
import { useRates } from '@/api/hooks';
import { formatDate, formatDateTime, formatNumber, formatRelative } from '@/lib/format';

function rateValue(rateE4: number): string {
  return `${formatNumber(rateE4 / 10_000, 2)} ₽`;
}

export function RatesCard() {
  const rates = useRates();
  const data = rates.data;

  const badge = !data ? null : data.failureStreak >= 3 ? (
    <Badge tone="danger" dot>
      Падает подряд
    </Badge>
  ) : data.error ? (
    <Badge tone="warning" dot>
      Последний снимок с ошибкой
    </Badge>
  ) : data.lastSuccessAt ? (
    <Badge tone="success" dot>
      Работает
    </Badge>
  ) : (
    <Badge tone="neutral">Не запускался</Badge>
  );

  return (
    <Card padding="md">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
          }}
        >
          Курс дня
        </div>
        {badge}
      </div>

      <div
        style={{
          marginTop: 'var(--space-5)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--space-5)',
        }}
      >
        <div>
          <Provenance>Сейчас для оплат с Bybit и без кошелька</Provenance>
          <div style={{ marginTop: 'var(--space-2)', font: '500 20px var(--font-mono)', color: 'var(--text-strong)' }}>
            {data?.current ? rateValue(data.current.rateE4) : '—'}
          </div>
          <Provenance>
            {!data
              ? 'Считаем'
              : !data.current
                ? 'Курса нет: снимков P2P и курса ЦБ ещё не собрано'
                : data.current.source === 'p2p'
                  ? `P2P Bybit, снимок ${formatDateTime(data.current.observedAt)}`
                  : `ЦБ, запасной: свежего снимка P2P нет`}
          </Provenance>
        </div>

        <div>
          <Provenance>Последний снимок P2P Bybit</Provenance>
          <div style={{ marginTop: 'var(--space-2)', font: '400 15px var(--font-mono)', color: 'var(--text-strong)' }}>
            {data?.p2p ? rateValue(data.p2p.rateE4) : '—'}
          </div>
          <Provenance>
            {data?.p2p
              ? `${formatRelative(data.p2p.observedAt)}` +
                (data.p2p.details?.ads
                  ? `, медиана ${data.p2p.details.ads} объявлений от ${formatNumber(data.p2p.details.amountRub ?? 0)} ₽`
                  : '')
              : 'Снимков ещё не было'}
          </Provenance>
        </div>

        <div>
          <Provenance>Курс ЦБ</Provenance>
          <div style={{ marginTop: 'var(--space-2)', font: '400 15px var(--font-mono)', color: 'var(--text-strong)' }}>
            {data?.cbr ? rateValue(data.cbr.rateE4) : '—'}
          </div>
          <Provenance>
            {data?.cbr
              ? `на ${formatDate(data.cbr.observedAt)}` + (data.cbr.origin ? `, источник: ${data.cbr.origin}` : '')
              : 'Курс ЦБ ещё не загружен'}
          </Provenance>
        </div>
      </div>

      {data?.error ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Provenance>{`Последняя ошибка: ${data.error}`}</Provenance>
        </div>
      ) : null}
      <div style={{ marginTop: 'var(--space-4)' }}>
        <Provenance>
          Снимок раз в час и по кнопке «Синхронизировать всё». Курс ЦБ — запасной: он ниже цены P2P
          на 1–3%.
        </Provenance>
      </div>
    </Card>
  );
}
